import { format } from 'date-fns';

export const APP_TIMEZONE = 'Europe/Rome';

const MS_PER_DAY = 86_400_000;

export function getLocalToday(tz: string = APP_TIMEZONE): Date {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number.parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);

  return new Date(get('year'), get('month') - 1, get('day'));
}

/** "Oggi" locale (`Europe/Rome`) come YYYY-MM-DD: confronto diretto con i `dueAt` del backend. */
export function getLocalTodayIso(tz: string = APP_TIMEZONE): string {
  return format(getLocalToday(tz), 'yyyy-MM-dd');
}

/**
 * Resolves ANY instant (a full ISO timestamp such as a record's `recordedAt`/
 * `measuredAt`/`startedAt`/`endedAt`) to the calendar day it belongs to in
 * `tz` (default Europe/Rome), as `YYYY-MM-DD` — the same business day the
 * backend assigns via `BusinessDay.fromInstant`. A late-UTC record (e.g.
 * 22:10 UTC in September, CEST) belongs to the NEXT local day; slicing the
 * ISO string or calling `toISOString()` reads UTC host parts and gets this
 * wrong in the 22:00–23:59 UTC window (00:00–01:59 CEST the next day).
 *
 * Idempotent on an already-local `YYYY-MM-DD` string: date-only ISO forms
 * parse as UTC midnight, and Rome is always at or ahead of UTC (0 to +2h),
 * so re-deriving the day can only ever land on the same date, never earlier.
 */
export function getLocalDateIso(instant: Date | string, tz: string = APP_TIMEZONE): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

const END_OF_DAY_HOUR = 23;
const END_OF_DAY_MINUTE = 59;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Offset of `tz` from UTC (ms) in force at `instant`, read back from the wall clock Intl prints. */
function getZoneOffsetMs(instant: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? '0', 10);
  const wallClockAsUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return wallClockAsUtcMs - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * The instant at which the calendar day `YYYY-MM-DD` ends (23:59:00) in `tz`, whatever the zone
 * of the browser. `new Date(y, m, d, 23, 59)` would build the end of day in the BROWSER zone: a
 * consultant travelling abroad would send a deadline one or more hours off from Rome. The offset
 * is read twice so a daylight-saving change inside the day lands on the right side of it.
 */
export function getLocalEndOfDayInstant(isoDay: string, tz: string = APP_TIMEZONE): Date | null {
  const match = DATE_ONLY_PATTERN.exec(isoDay);
  if (!match) {
    return null;
  }
  const wallClockAsUtcMs = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    END_OF_DAY_HOUR,
    END_OF_DAY_MINUTE,
  );
  const firstGuessMs = wallClockAsUtcMs - getZoneOffsetMs(new Date(wallClockAsUtcMs), tz);
  return new Date(wallClockAsUtcMs - getZoneOffsetMs(new Date(firstGuessMs), tz));
}

function getAppTodayUtcMs(today: Date): number {
  return Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
}

function getReferenceDayUtcMs(reference: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(reference);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number.parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return Date.UTC(get('year'), get('month') - 1, get('day'));
}

function parseDateAtUtcMidnight(input: string | Date): number | null {
  // Strings carry a calendar date (lot expiry/production, @db.Date serialized) whose
  // leading YYYY-MM-DD is authoritative — use the literal components, ignoring any
  // trailing time/Z. Date objects instead are live timestamps (e.g. a non-compliance
  // `createdAt`); resolve those through APP_TIMEZONE so a 22:30 UTC record (= 00:30
  // CEST next day) maps to the local day the user lived. getUTC* on a Date alone
  // off-by-ones against getLocalToday() in the 00:00–01:59 CEST window.
  if (typeof input === 'string') {
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
    if (ymd) {
      return Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    }
    const parsedFromString = new Date(input);
    if (Number.isNaN(parsedFromString.getTime())) {
      return null;
    }
    return getReferenceDayUtcMs(parsedFromString, APP_TIMEZONE);
  }
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) {
      return null;
    }
    return getReferenceDayUtcMs(input, APP_TIMEZONE);
  }
  return null;
}

export function diffDaysFromAppToday(
  date: string | Date | null | undefined,
  reference?: Date,
): number | null {
  if (date == null) {
    return null;
  }
  const dateMs = parseDateAtUtcMidnight(date);
  if (dateMs === null) {
    return null;
  }
  const todayMs = reference
    ? getReferenceDayUtcMs(reference, APP_TIMEZONE)
    : getAppTodayUtcMs(getLocalToday());
  return Math.round((dateMs - todayMs) / MS_PER_DAY);
}
